/**
 * German surface strings for machine output.
 *
 * Tool names arrive as identifiers (`schadenshistorie_objekt`). A Verwalter should
 * read what was looked up, not the function that looked it up, so every tool the
 * assistant can reach has a label written the way the work is named in the office.
 * Unknown tools fall back to a de-underscored form rather than to the raw identifier,
 * because the catalogue grows in a directory this file does not own.
 */

const WERKZEUG_LABELS: Record<string, string> = {
  // Bestand
  liegenschaften_suchen: "Liegenschaften durchsucht",
  liegenschaft_details: "Objektakte gelesen",
  objektakte_notizen: "Notizen der Objektakte gelesen",
  bestandverhaeltnisse_liste: "Bestandverhältnisse gelesen",
  portfolio_kennzahlen: "Portfolio-Kennzahlen gerechnet",

  // Geld
  offene_posten: "Offene Posten gerechnet",
  offene_posten_altersstruktur: "Altersstruktur der offenen Posten gerechnet",
  vorschreibungen_liste: "Vorschreibungen gelesen",
  vorschreibungslauf_details: "Vorschreibungslauf gelesen",
  zinsliste_monat: "Zinsliste des Monats gelesen",
  kontoauszug: "Kontoauszug gelesen",
  abrechnungen_uebersicht: "Abrechnungen gelesen",
  betriebskostenabrechnung_details: "Betriebskostenabrechnung gelesen",

  // Betrieb
  schaeden_liste: "Schadensfälle gelesen",
  schaden_akte: "Schadensakte gelesen",
  schadenshistorie_objekt: "Schadensgeschichte des Objekts gelesen",
  fristen_offen: "Offene Fristen geprüft",
  fristen_cockpit: "Fristenübersicht gelesen",
  vergabe_pruefung: "Vergabe geprüft",

  // Suche und Belege
  suche_daten: "Datenbestand durchsucht",
  suche_dokumente: "Dokumente durchsucht",
  grep_belege: "Belege durchsucht",
  lies_dokument: "Dokument gelesen",
  pruefpfad: "Prüfpfad aufgelöst",

  // Kern. These two are rendered as a plan and as a fan-out rather than as steps;
  // the labels exist so nothing falls back to an identifier if that ever changes.
  plan_setzen: "Plan fortgeschrieben",
  teilauftrag_starten: "Teilaufträge vergeben",
  gedaechtnis_lesen: "Dauerhafte Notizen gelesen",
  gedaechtnis_notieren: "Dauerhafte Notiz vorgeschlagen",
};

export function werkzeugLabel(name: string): string {
  const bekannt = WERKZEUG_LABELS[name];
  if (bekannt) return bekannt;
  const worte = name.replace(/_/g, " ").trim();
  return worte.charAt(0).toUpperCase() + worte.slice(1);
}

/** What a tool's `art` means, for the expanded body. */
export const ART_LABELS: Record<string, string> = {
  suche: "Suche",
  lesen: "Lesen",
  rechnen: "Rechnen",
  entwurf: "Entwurf",
  schreiben: "Schreiben",
  senden: "Senden",
  lauf: "Lauf",
};

/** Risk classes, spelled out. `frei` is not shown at all — it is the normal case. */
export const RISIKO_LABELS: Record<string, string> = {
  freigabe: "Freigabe erforderlich",
  vier_augen: "Vier-Augen-Prinzip",
  verboten: "Nicht ausführbar",
};

const STATUS_LABELS: Record<string, string> = {
  geplant: "Geplant",
  laeuft: "Arbeitet",
  wartet_auf_freigabe: "Wartet auf Freigabe",
  erfolgreich: "Fertig",
  fehlgeschlagen: "Fehlgeschlagen",
  abgebrochen: "Abgebrochen",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/**
 * The failure, in German.
 *
 * The kernel stores whatever went wrong verbatim, and for a model call that is an
 * English sentence from the provider. Showing it raw tells a Verwalter nothing about
 * whether the fault is theirs, so the recognisable cases are named in German and the
 * original is kept underneath as the technical detail.
 */
export interface UebersetzterFehler {
  satz: string;
  /** The untranslated original, shown only when the user opens the detail. */
  roh: string | null;
  /** False when nothing matched and `satz` is only the generic fallback. */
  erkannt: boolean;
}

export function uebersetzeFehler(code: string | null, text: string | null): UebersetzterFehler {
  const roh = text && text.trim() ? text.trim() : null;
  const treffer = (satz: string): UebersetzterFehler => ({ satz, roh, erkannt: true });

  if (code === "zuege_erschoepft") {
    return treffer("Der Lauf wurde angehalten: das Zugbudget für Modellaufrufe ist ausgeschöpft.");
  }
  if (code === "budget_erschoepft") {
    return treffer("Der Lauf wurde angehalten: das Kostenbudget ist ausgeschöpft.");
  }

  if (roh) {
    if (/credit balance is too low|insufficient.*credit/i.test(roh)) {
      return treffer(
        "Der Zugang zum Sprachmodell ist nicht bezahlt: das Guthaben des hinterlegten " +
          "API-Kontos reicht nicht aus. Das ist keine Fehleingabe und liegt nicht an Ihren Daten.",
      );
    }
    if (/invalid x-api-key|authentication_error/i.test(roh)) {
      return treffer(
        "Der Zugang zum Sprachmodell wurde abgelehnt. Der hinterlegte Schlüssel gilt nicht.",
      );
    }
    if (/rate.?limit|\b429\b/i.test(roh)) {
      return treffer(
        "Das Sprachmodell hat die Anfrage wegen zu vieler Zugriffe abgewiesen. " +
          "Bitte in einigen Minuten erneut versuchen.",
      );
    }
    if (/overloaded|\b529\b|\b503\b/i.test(roh)) {
      return treffer("Das Sprachmodell ist derzeit überlastet. Bitte später erneut versuchen.");
    }
    if (/not_found_error/i.test(roh)) {
      return treffer("Das eingestellte Sprachmodell ist unter diesem Namen nicht erreichbar.");
    }
  }

  return { satz: "Der Lauf ist fehlgeschlagen.", roh, erkannt: false };
}

/**
 * One line for a failure inside the trace.
 *
 * A recognised provider fault is replaced by its German sentence; anything else is the
 * kernel's own message, which is already German and already specific. Truncated,
 * because a step in the trace is one line — the full text lives in the failure block.
 */
export function kurzfehler(text: string): string {
  const uebersetzt = uebersetzeFehler(null, text);
  const satz = uebersetzt.erkannt ? uebersetzt.satz : text.replace(/\s+/g, " ").trim();
  return satz.length > 160 ? `${satz.slice(0, 160)}…` : satz;
}

const GELD = new Intl.NumberFormat("de-AT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

/** Cents to euro. Every amount in this codebase is an integer number of cents. */
export function euro(cent: number): string {
  return GELD.format(cent / 100);
}

const ZEIT = new Intl.DateTimeFormat("de-AT", { hour: "2-digit", minute: "2-digit" });

export function uhrzeit(wert: string | Date): string {
  const datum = typeof wert === "string" ? new Date(wert) : wert;
  return Number.isNaN(datum.getTime()) ? "" : ZEIT.format(datum);
}
